import React, { AnchorHTMLAttributes, useContext } from "react";
import { cloneDeep } from "src/services/collections";
import { useQuery } from "@apollo/client";
import XXH from "xxhashjs";
import { OFFERS_QUERY } from "src/gql/queries";
import { SubscriptionContext } from "./SubscriptionService";

export const splitParagraphs = (text: string | null, className: string = "offer-text") => {
  if (!text) {
    return null;
  }

  return text.split("\n").map((line: string, idx: number) => (
    <p className={className} key={idx}>
      {line}
    </p>
  ));
};

export interface OfferItem {
  prismic_title: string;
  welcome_offer: string; // validated
  rating: number; // validated
  staff_pick: "true" | "false";
  web_link: string; // validated (web only)
  promo_code: string | null;
  how_it_works: string | null;
  how_it_works_rich: any[] | null;
  preferred_payment: string | null;
  unpreferred_payment: string | null;
  withdrawal_time: string | null;
  minimum_deposit: string | null;
  new_badge: boolean | null;
  minimum_withdrawal: string | null;
  recommended_payment: string | null;
  review_content: any[] | null;
  sportsbook_name: string;
  image: { url: string }; // validated
  _meta: { id: string };
}

export interface StateItem {
  name: string;
  abbreviation: string;
  offers_title: string;
  offers_hero_content?: any[] | null;
  offers_description: string;
  offers_hero_title: string;
  offers_hero_subtitle: string;
  offers_content: any[];
  sportsbook_offers: OfferItem[];
  casino_offers_hero_content?: any[] | null;
  casino_offers_title: string;
  casino_offers_description: string;
  casino_offers_hero_title: string;
  casino_offers_hero_subtitle: string;
  casino_offers_content: any[];
  casino_offers: OfferItem[];
}

export const getOfferId = (offer: OfferItem) => {
  return XXH.h32(JSON.stringify(offer.web_link), 0).toString(16);
};

/**
 * Validate and filter offers from Prismic
 */
const filterInvalidOffers = (offers: OfferItem[]) =>
  offers.filter((offer: OfferItem) => {
    if (!offer) {
      return false;
    }
    if (!offer.image?.url) {
      return false;
    }
    if (!offer.welcome_offer) {
      return false;
    }
    if (!offer.rating) {
      return false;
    }
    if (!offer.web_link) {
      return false;
    }
    return true;
  });

// Prepare prismic offer states response for frontend
export const prepareOfferStates = (response: any): { data: StateItem[]; loading: boolean } => {
  // Get list of states
  let states: StateItem[] = cloneDeep(
    response?.data?.prismicAllStatess?.edges?.[0]?.node?.states?.map((s: any) => s.state) ?? []
  );

  // Transform StateItem to proper data structure
  states.forEach((state) => {
    state.sportsbook_offers = state?.sportsbook_offers?.map((o: any) => o.sportsbook_offer) ?? [];
    state.casino_offers = state?.casino_offers?.map((o: any) => o.sportsbook_offer) ?? [];
  });

  // Validate prismic entries
  states.forEach((state) => {
    state.sportsbook_offers = filterInvalidOffers(state.sportsbook_offers);
    state.casino_offers = filterInvalidOffers(state.casino_offers);
  });

  return { data: states, loading: response.loading };
};

// Easy hook for getting offer states
export const useOfferStates = ({
  ssr = false,
}: {
  ssr?: boolean;
} = {}): { data: StateItem[]; loading: boolean } => {
  const response = useQuery(OFFERS_QUERY, { ssr });
  return prepareOfferStates(response);
};

interface MetaBetLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  bookName: string;
  sportsdataGameId: string;
}

export const MetaBetLink = ({ bookName, sportsdataGameId, children, ...props }: MetaBetLinkProps) => {
  const ctx = useContext(SubscriptionContext);

  if (!bookName) {
    return null;
  }

  const cleanBookName = bookName.toLowerCase().replace(" ", "").replace("-", "");
  let href = `/api/go?b=${cleanBookName}&s=${ctx.userLocale}`;

  if (sportsdataGameId) {
    href += `&g=${sportsdataGameId}`;
  }

  return (
    <a {...props} href={href}>
      {children}
    </a>
  );
};

export const AffiliateLink = ({ children, href, ...props }: AnchorHTMLAttributes<HTMLAnchorElement>) => {
  return (
    <a {...props} href={href}>
      {children}
    </a>
  );
};
